import { useState } from 'react';
import { useThemeColors } from '@/shared/theme/useThemeColors';
import { semantic, radius } from '@/shared/theme/tokens';

/**
 * IconButton — 44×44px touch-target wrapper for material-symbols icons.
 * Replaces the 15+ bare icon button patterns across page files.
 *
 * Props:
 *   icon      — material-symbols name
 *   onClick   — click handler
 *   variant   — 'default' | 'danger' | 'warning' | 'ghost'
 *   size      — icon size in px (default 16)
 *   title     — tooltip / aria-label
 *   disabled  — disabled state
 *   filled    — fill icon (fontVariationSettings FILL 1)
 *   style     — additional container style overrides
 */
export function IconButton({
  icon,
  onClick,
  variant = 'default',
  size = 16,
  title,
  disabled = false,
  filled = false,
  style,
}) {
  const c = useThemeColors();
  const [hovered, setHovered] = useState(false);

  const variantStyles = {
    default: {
      color:      hovered ? c.textActive : c.textMuted,
      bg:         hovered ? c.bgHover : 'transparent',
      border:     hovered ? `1px solid ${c.border}` : '1px solid transparent',
    },
    danger: {
      color:      hovered ? semantic.error : c.textMuted,
      bg:         hovered ? semantic.errorContainer : 'transparent',
      border:     '1px solid transparent',
    },
    warning: {
      color:      hovered ? semantic.warning : c.textMuted,
      bg:         hovered ? semantic.warningBg : 'transparent',
      border:     '1px solid transparent',
    },
    ghost: {
      color:      c.textSubtle,
      bg:         'transparent',
      border:     '1px solid transparent',
    },
  };

  const vs = variantStyles[variant] ?? variantStyles.default;

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      disabled={disabled}
      title={title}
      aria-label={title}
      style={{
        width: '44px',
        height: '44px',
        minWidth: '44px',
        borderRadius: radius.md,
        background: vs.bg,
        border: vs.border,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        flexShrink: 0,
        opacity: disabled ? 0.4 : 1,
        transition: 'all 0.15s',
        ...style,
      }}
    >
      <span
        className="material-symbols-outlined"
        style={{
          fontSize: size,
          color: vs.color,
          fontVariationSettings: filled ? "'FILL' 1" : "'FILL' 0",
          transition: 'color 0.15s',
          userSelect: 'none',
        }}
      >
        {icon}
      </span>
    </button>
  );
}
